/**
 * TKP-196 — form bölümlerinde kip (Ekle / Üzerine yaz / Kaldır): form hazırlama kipinde yükü verir, panel hedef başına adımları
 * buradan alır. Eşleşme ana alanla: işleme = amaç + hukuki sebep çifti, aktarım = üçüncü taraf, erişim = erişen kişi, saklama = ortam.
 *  ADD      → bugünkü ekleme (olan atlanır / backend tekilliği),
 *  OVERRIDE → hedefteki bölüm kayıtlarının tümü silinir, formdaki yazılır (işleme: satırın kümesi formdakiyle değişir),
 *  REMOVE   → ana alanla eşleşen kayıtlar silinir (işleme: kümeden düşülür), yeni yazım yok.
 * Saf; adım: { action: "create" | "update" | "delete", store, payload | id }.
 */
import { processingLegalWrites, transferingWrites } from "@/modules/process-data/inventory-panel";
import { storageWritePlan } from "@/modules/inventory/helpers/storage-retention";

export const KIPS = ["ADD", "OVERRIDE", "REMOVE"];
const sid = (x) => String(x && x._id ? x._id : x || "");
const norm = (v) => String(v || "").trim().toLocaleLowerCase("tr");
const sameName = (a, b) => {
  if (!a || !b) return false;
  if (typeof a === "string" || typeof b === "string") return norm(typeof a === "string" ? a : a.tr) === norm(typeof b === "string" ? b : b.tr);
  return Object.keys(a).some((k) => a[k] && b[k] && norm(a[k]) === norm(b[k]));
};

/** İşleme kaydının anahtarı — amaç ya da hukuki sebep boş olabilir (TKP-196 kriter 1). */
export const processingKey = (i) => `${sid(i && i.purpose && (i.purpose.libraryItem || i.purpose))}|${sid(i && i.legalReason && (i.legalReason.libraryItem || i.legalReason))}`;

/** İşleme: satır başına küme işlemi. `inventory[rowId].processingLegals` mevcut küme. */
export const processingKipPlan = ({ editOption = "ADD", rowIds = [], items = [], inventory = {} }) => {
  const rows = (rowIds || []).map(sid).filter(Boolean);
  if (editOption === "ADD") return processingLegalWrites(rows, items).map((payload) => ({ action: "create", store: "datasubjectProcessingLegal/create", payload }));
  const existingOf = (rowId) => ((inventory[rowId] || {}).processingLegals || []).filter((x) => x && !x.__draft);
  const strip = (x) => ({ purpose: x.purpose, legalReason: x.legalReason, legitimateInterest: x.legitimateInterest });
  const steps = [];
  for (const rowId of rows) {
    const existing = existingOf(rowId);
    const keys = new Set((items || []).map(processingKey));
    const next = editOption === "OVERRIDE" ? (items || []).map(strip) : existing.filter((x) => !keys.has(processingKey(x))).map(strip);
    if (editOption === "REMOVE" && next.length === existing.length) continue; // eşleşen yok
    steps.push({ action: "update", store: "datasubjectProcessingLegal/update", payload: { datasubject: { _id: rowId }, items: next.map((i) => ({ ...i, datasubject: { _id: rowId } })) } });
  }
  return steps;
};

/** Aktarım: satır × üçüncü taraf. `inventory[rowId].transferings` mevcut kayıtlar ({ _id, thirdParty }). */
export const transferingKipPlan = ({ editOption = "ADD", rowIds = [], prepared = {}, inventory = {} }) => {
  const rows = (rowIds || []).map(sid).filter(Boolean);
  const creates = () => transferingWrites(rows, prepared).map((payload) => ({ action: "create", store: "datasubjectTransfering/create", payload }));
  if (editOption === "ADD") return creates();
  const thirdParties = (Array.isArray(prepared.thirdParty) ? prepared.thirdParty : prepared.thirdParty ? [prepared.thirdParty] : []).map(sid);
  const existingOf = (rowId) => ((inventory[rowId] || {}).transferings || []).filter((x) => x && x._id && !x.__draft);
  const steps = [];
  for (const rowId of rows) {
    for (const x of existingOf(rowId)) {
      if (editOption === "OVERRIDE" || thirdParties.includes(sid(x.thirdParty))) steps.push({ action: "delete", store: "datasubjectTransfering/delete", id: x._id });
    }
  }
  return editOption === "OVERRIDE" ? [...steps, ...creates()] : steps;
};

/** Erişim: hedef (veri ya da satır) × erişen kişi. Mevcut: `nodeInventory[n].accessAndRestrictions` / `datasubjectInventory[r].accessAndRestrictions`. */
export const accessKipPlan = ({ editOption = "ADD", nodeIds = [], rowIds = [], payload = {}, nodeInventory = {}, datasubjectInventory = {} }) => {
  const targets = [...(nodeIds || []).map(sid).filter(Boolean).map((nodeId) => ({ nodeId })), ...(rowIds || []).map(sid).filter(Boolean).map((rowId) => ({ rowId }))];
  const level = (t) => (t.rowId ? { datasubject: { _id: t.rowId } } : { processData: { _id: t.nodeId } });
  // formun yüke kattığı seviye alanları (editedData.processData, data) düşülür — kayıt tam olarak BİR seviye taşımalı (TKP-195)
  const { processData: _pd, datasubject: _ds, data: _d, datasource: _src, ...body } = payload || {};
  const existingOf = (t) => (((t.rowId ? datasubjectInventory[t.rowId] : nodeInventory[t.nodeId]) || {}).accessAndRestrictions || []).filter((x) => x && x._id && !x.__draft);
  const matches = (x) => sameName(x.accessor && x.accessor.name, payload.libraryItem && payload.libraryItem.name);
  const steps = [];
  for (const t of targets) {
    const existing = existingOf(t);
    if (editOption === "REMOVE") {
      for (const x of existing) if (matches(x)) steps.push({ action: "delete", store: "dataAccessAndRestriction/delete", id: x._id });
      continue;
    }
    if (editOption === "OVERRIDE") for (const x of existing) steps.push({ action: "delete", store: "dataAccessAndRestriction/delete", id: x._id });
    if (editOption === "ADD" && existing.some(matches)) continue;
    steps.push({ action: "create", store: "dataAccessAndRestriction/save", payload: { ...body, ...level(t) } });
  }
  return steps;
};

/** Saklama: hedef × ortam; ADD'de ortam zaten varsa süre alanları o kayda (TKP-182 md.28/29). `ownOf(t)` hedefin mevcut saklama kayıtları. */
export const storageKipPlan = ({ editOption = "ADD", store, kind = "physical", nodeIds = [], rowIds = [], payload = {}, ownOf = () => [] }) => {
  const targets = [...(nodeIds || []).map(sid).filter(Boolean).map((nodeId) => ({ nodeId })), ...(rowIds || []).map(sid).filter(Boolean).map((rowId) => ({ rowId }))];
  const level = (t) => (t.rowId ? { datasubject: { _id: t.rowId } } : { processData: { _id: t.nodeId } });
  const { processData: _pd, datasubject: _ds, data: _d, datasource: _src, ...body } = payload || {}; // seviye alanları hedeften gelir
  const items = Array.isArray(body.libraryItem) ? body.libraryItem : body.libraryItem ? [body.libraryItem] : [];
  const itemIds = items.map(sid);
  // TKP-222 p.3/4: ortam seçilmeden Kaldır → ortamsız kayıtlar süreye göre (süre verildiyse aynı süre) eşleşir
  const noEnv = (x) => !x.libraryItem && !Object.values((x && x.name) || {}).some((n) => String(n || "").trim());
  const period = Number(body.period) > 0 ? Number(body.period) : null;
  const matches = (x) => (items.length ? itemIds.includes(sid(x.libraryItem)) || items.some((i) => sameName(x.name, i.name)) : noEnv(x) && (period === null || Number(x.period) === period));
  const steps = [];
  for (const t of targets) {
    const own = (ownOf(t) || []).filter((x) => x && x._id && !x.__draft);
    if (editOption === "REMOVE") {
      for (const x of own) if (matches(x)) steps.push({ action: "delete", store: `${store}/delete`, id: x._id });
      continue;
    }
    if (editOption === "OVERRIDE") {
      for (const x of own) steps.push({ action: "delete", store: `${store}/delete`, id: x._id });
      for (const step of storageWritePlan({ payload: { ...body, ...level(t) }, items, ownItems: [], kind })) steps.push({ action: "create", store: `${store}/save`, payload: step.payload });
      continue;
    }
    for (const step of storageWritePlan({ payload: { ...body, ...level(t) }, items, ownItems: own, kind })) {
      if (step.action === "update") steps.push({ action: "update", store: "datasourcePhysicalStorage/updateRetention", payload: step.payload });
      else if (step.action === "create") steps.push({ action: "create", store: `${store}/save`, payload: step.payload });
    }
  }
  return steps;
};

/**
 * TKP-196 — tedbirler (düğüm alanı / satır alanı): formun listeleri hedefin listeleriyle kipe göre birleşir.
 *  ADD → birleşim, OVERRIDE → formdaki liste, REMOVE → formdakiler çıkarılır. `existingOf(t)` → { administrativeMeasures, technicalMeasures }.
 * Düğüm → `organizationProcessData/update` ({ _id, administrativeMeasures, technicalMeasures }), satır → `organizationProcessDataDatasubject/updateFeature`.
 */
export const measuresKipPlan = ({ editOption = "ADD", nodeIds = [], rowIds = [], form = {}, existingOf = () => ({}) }) => {
  const ref = (i) => (i && i._id ? { _id: String(i._id) } : i ? { _id: String(i) } : null);
  const refs = (list) => (list || []).map(ref).filter(Boolean);
  const merge = (existing, picked) => {
    const ex = refs(existing); const pk = refs(picked);
    if (editOption === "OVERRIDE") return pk;
    const ids = new Set(pk.map((x) => x._id));
    if (editOption === "REMOVE") return ex.filter((x) => !ids.has(x._id));
    return [...ex, ...pk.filter((x) => !ex.some((e) => e._id === x._id))];
  };
  const targets = [...(nodeIds || []).map(sid).filter(Boolean).map((nodeId) => ({ nodeId })), ...(rowIds || []).map(sid).filter(Boolean).map((rowId) => ({ rowId }))];
  const same = (a, b) => a.length === b.length && a.every((x) => b.some((y) => y._id === x._id));
  return targets.flatMap((t) => {
    const cur = existingOf(t) || {};
    const payload = { _id: t.rowId || t.nodeId, administrativeMeasures: merge(cur.administrativeMeasures, form.administrativeMeasures), technicalMeasures: merge(cur.technicalMeasures, form.technicalMeasures) };
    // TKP-200 m.17: değişmeyen hedefe yazım yok (onay açıkken her yazım taslak satırı ve kilit demek)
    if (same(payload.administrativeMeasures, refs(cur.administrativeMeasures)) && same(payload.technicalMeasures, refs(cur.technicalMeasures))) return [];
    return [{ action: "update", store: t.rowId ? "organizationProcessDataDatasubject/updateFeature" : "organizationProcessData/update", payload }];
  });
};

